import * as SAT from 'sat';
import { CardModifierType } from '../../../Prototype/Level/Card/CardModifierType';
import { CardStatus } from '../../../Prototype/Level/Card/CardStatus';
import { ICardModifierData } from '../../../Prototype/Level/Card/ICardModifierData';
import { IGameState } from '../../Game/IGameState';
import { ICardStateData } from '../CardReducer';
import CardStateHelper from '../CardStateHelper';
import CardStateModifierHelper from '../CardStateModifierHelper';

export default class BaseCardBehavior {
    public cardID: string;

    constructor(cardID: string) {
        this.cardID = cardID;
    }

    public isFaceUp(state: IGameState): boolean {
        return state.levelState.cardsState.cards[this.cardID].faceUp;
    }

    public isPlayable(state: IGameState, currentCard: string): boolean {
        const cardState = CardStateHelper.getCardStatus(state, this.cardID);

        switch (cardState) {
            case CardStatus.IN_PLAY:
                // Must be face-up and not covered by another card and non-blocked by a modifier.
                return !this.isBlocked(state);
            case CardStatus.IN_STACK:
                // Must be top of "stack".
                return (
                    this.cardID ===
                    state.levelState.cardsState.inStack[
                        state.levelState.cardsState.inStack.length - 1
                    ]
                );
            case CardStatus.FOLDED:
            case CardStatus.DISCARDED:
            case CardStatus.UNKNOWN:
                return false;
        }

        return true;
    }

    public isMatch(state: IGameState, cardB: string): boolean {
        return true;
    }

    public play(state: IGameState): IGameState {
        return state;
    }

    /**
     * Called when a 'valid' turn is completed.
     */
    public update(state: IGameState): IGameState {
        return state;
    }

    /**
     * Check if cardA is above cardB and cardA's bounding box is intersecting cardB's bounding box.
     * @param state
     * @param card The target card to compare.
     */
    public isOverlapping(state: IGameState, targetCardID: string) {
        // Cannot overlap itself.
        if (this.cardID === targetCardID) return false;

        const cardDataA: ICardStateData =
            state.levelState.cardsState.cards[this.cardID];
        const cardDataB: ICardStateData =
            state.levelState.cardsState.cards[targetCardID];

        // Target card must be below this card.
        let deltaDepth: number = cardDataA.depth - cardDataB.depth;
        if (deltaDepth <= 0) return false;

        const a = new SAT.Polygon(new SAT.Vector(0, 0), [
            new SAT.Vector(-cardDataA.width * 0.5, -cardDataA.height * 0.5),
            new SAT.Vector(cardDataA.width * 0.5, -cardDataA.height * 0.5),
            new SAT.Vector(cardDataA.width * 0.5, cardDataA.height * 0.5),
            new SAT.Vector(-cardDataA.width * 0.5, cardDataA.height * 0.5),
        ]);
        a.rotate(cardDataA.rotation);
        a.setOffset(new SAT.Vector(cardDataA.x, cardDataA.y));

        const b = new SAT.Polygon(new SAT.Vector(0, 0), [
            new SAT.Vector(-cardDataB.width * 0.5, -cardDataB.height * 0.5),
            new SAT.Vector(cardDataB.width * 0.5, -cardDataB.height * 0.5),
            new SAT.Vector(cardDataB.width * 0.5, cardDataB.height * 0.5),
            new SAT.Vector(-cardDataB.width * 0.5, cardDataB.height * 0.5),
        ]);
        b.rotate(cardDataB.rotation);
        b.setOffset(new SAT.Vector(cardDataB.x, cardDataB.y));

        return SAT.testPolygonPolygon(a, b);
    }

    /**
     * Return true if the specified card is covered/overlapped by other in-play cards.
     */
    public isObscured(state: IGameState): boolean {
        for (let otherCardID of state.levelState.cardsState.inPlay) {
            const behavior = CardStateHelper.getCardBehavior(
                state,
                otherCardID
            );
            if (behavior.isOverlapping(state, this.cardID)) {
                return true;
            }
        }

        return false;
    }

    /**
     * Return true if card is face down or card is obscured or any of this card's modifiers will block this card from being played.
     */
    public isBlocked(
        state: IGameState,
        exceptions?: CardModifierType[]
    ): boolean {
        return (
            !this.isFaceUp(state) ||
            this.isObscured(state) ||
            this.modifiersBlocking(state, exceptions)
        );
    }

    /**
     * Check if any of this card's modifiers will block this card from being played.
     */
    private modifiersBlocking(
        state: IGameState,
        exceptions?: CardModifierType[]
    ): boolean {
        return state.levelState.cardsState.cards[this.cardID].modifiers.some(
            (modifier: ICardModifierData) => {
                if (exceptions && exceptions.includes(modifier.type)) {
                    return false;
                }
                const behavior = CardStateModifierHelper.getModifierBehavior(
                    state,
                    modifier,
                    this.cardID
                );
                return behavior.isBlocking(state);
            }
        );
    }
}
